/**
 * The HTTP layer, ported from the old app.js without changing what it does.
 *
 * The session cookie set by /v1/auth/verify is the real credential and rides
 * along automatically on a same-origin fetch. The bearer token in localStorage
 * is the developer escape hatch -- a script, a curl session, a local debugging
 * run -- and is sent only when one exists.
 */

const TOKEN_KEY = 'unomena-docs-token'
/** Counts /login round-trips in this tab, so a refusal loop cannot spin. */
const BOUNCE_KEY = 'unomena-docs-login-bounce'

/**
 * The developer escape hatch, not the way people sign in.
 *
 * Reading `?token=` also strips it from the address bar, where it would
 * otherwise reach history, bookmarks and any copied link. BUG-007.
 */
export function token(): string | null {
  const fromUrl = new URLSearchParams(location.search).get('token')
  if (fromUrl) {
    try {
      localStorage.setItem(TOKEN_KEY, fromUrl)
    } catch {
      /* private mode, or storage is full: the request still carries the token */
    }
    // Keep everything else in the query string; only the credential goes.
    const params = new URLSearchParams(location.search)
    params.delete('token')
    const rest = params.toString()
    history.replaceState({}, '', location.pathname + (rest ? `?${rest}` : ''))
    return fromUrl
  }
  try {
    // No 'dev-token' fallback: a published default meant every unauthenticated
    // visit tried a known credential. BUG-007.
    return localStorage.getItem(TOKEN_KEY) || null
  } catch {
    return null
  }
}

export function forgetToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* nothing to forget, or nowhere to forget it from */
  }
}

export function bounceCount(): number {
  try {
    return Number(sessionStorage.getItem(BOUNCE_KEY)) || 0
  } catch {
    return 0
  }
}

export function recordBounce(n: number): void {
  try {
    sessionStorage.setItem(BOUNCE_KEY, String(n))
  } catch {
    /* without storage the loop guard degrades to "no guard", not to a crash */
  }
}

export function clearBounce(): void {
  try {
    sessionStorage.removeItem(BOUNCE_KEY)
  } catch {
    /* see recordBounce */
  }
}

/**
 * A refusal a caller can branch on.
 *
 * The API answers every failure with {verdict:'deny', code, path, message}.
 * Throwing `new Error(status + ' ' + rawBody)` meant callers either printed
 * JSON at the user or sniffed the status out of a message with a regex, so the
 * status, the refusal code and its path are kept as fields. TASK-034.
 */
export class ApiError extends Error {
  status: number
  code: string | null
  path: string[]
  detail: string
  body: Record<string, any> | null
  /** Seconds, from the Retry-After header. 0 when we were not told. */
  retryAfter: number

  constructor(status: number, body: any, text?: string, retryAfter = 0) {
    const detail = (body && body.message) || String(text || '').slice(0, 300)
    super(`${status} ${detail}`.trim())
    this.name = 'ApiError'
    this.status = status
    this.code = (body && body.code) || null
    this.path = (body && body.path) || []
    this.detail = detail
    this.body = body || null
    this.retryAfter = retryAfter
  }
}

/** Whether a refusal is the server asking for a slower caller. */
export function isRateLimited(err: unknown): boolean {
  const e = err as any
  return Boolean(e && (e.status === 429 || e.code === 'rate_limited'))
}

/** How long a rate-limited caller should wait, in plain words. */
export function inWords(seconds: number): string {
  if (!seconds) return 'in a moment'
  if (seconds < 60) return `in ${seconds} second${seconds === 1 ? '' : 's'}`
  const mins = Math.ceil(seconds / 60)
  return `in ${mins} minute${mins === 1 ? '' : 's'}`
}

export function isAuthError(err: unknown): boolean {
  const e = err as any
  if (e && e.status === 401) return true
  const denied = (e && e.status === 403) || /^(401|403)\b/.test(String(e && e.message))
  if (!denied) return false
  // The API answers "your token is no good" and "no such doc for you" with the
  // same 403 and the same code 'access'; only the refusal path separates them.
  // Sending someone to a sign-in box because they mistyped a document id is
  // its own dead end, so a refusal that names a ref is not an auth error.
  //
  // 'cookie' is the token endpoints' way of saying "not signed in in a
  // browser", which is exactly the case the sign-in page fixes.
  const path = (e && e.path) || []
  return path.length ? path[0] === 'authorization' || path[0] === 'cookie' : true
}

/** A document that is gone, or was never visible to this principal. */
export function isMissingError(err: unknown): boolean {
  const e = err as any
  if (!e) return false
  if (e.status === 404) return true
  const path = e.path || []
  return e.code === 'access' && path[0] === 'ref'
}

/**
 * Whether an endpoint simply is not deployed yet.
 *
 * A 404 with no refusal body is Fastify saying the route does not exist, which
 * is different from the API saying you may not see something. /v1/tokens is
 * being built in parallel with this page, so the difference is load-bearing.
 */
export function isNotDeployed(err: unknown): boolean {
  const e = err as any
  return Boolean(e && e.status === 404 && e.code === null)
}

/** One sentence a person can act on, never a JSON blob. */
export function describeError(err: unknown): string {
  if (!err) return 'Something went wrong.'
  if (isAuthError(err)) return 'you are not signed in, or your session has expired'
  if (isMissingError(err)) return "it no longer exists, or you don't have access"
  if (err instanceof ApiError) return err.detail || `the server answered ${err.status}`
  // fetch() rejects with a TypeError when the network itself fails.
  if (err instanceof TypeError) return 'the server could not be reached'
  return String((err as any).message || err) || 'Something went wrong.'
}

export interface ApiOptions extends RequestInit {
  /**
   * Send the session cookie and nothing else.
   *
   * The /v1/tokens endpoints refuse ANY request carrying an Authorization
   * header, even alongside a valid session cookie, so that a leaked agent
   * token can never mint more tokens for itself. A same-origin fetch sends the
   * cookie on its own, so suppressing the header is the whole fix -- but it
   * has to be deliberate, because this helper otherwise attaches a developer
   * bearer whenever localStorage holds one.
   */
  sessionOnly?: boolean
}

export async function api<T = any>(path: string, options: ApiOptions = {}): Promise<T> {
  const { sessionOnly, ...init } = options
  const bearer = sessionOnly ? null : token()
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) || {}),
  }
  /*
   * Only when there is something to describe.
   *
   * The old app.js set this header unconditionally, including on the bodyless
   * `POST /v1/events/:docId/ticket`. Fastify refuses a request that declares a
   * JSON body and sends none (FST_ERR_CTP_EMPTY_JSON_BODY), and the server's
   * catch-all turns that into a 500 -- so minting an SSE ticket has been
   * failing, and with it every live update, since before this rewrite. The
   * same POST with no content-type succeeds.
   *
   * Fixed here rather than in the server because sending content-type for a
   * body that does not exist is the client being wrong, and packages/server is
   * not this change's to alter beyond its static routes.
   */
  if (init.body !== undefined && init.body !== null && !headers['content-type']) {
    headers['content-type'] = 'application/json'
  }
  // Only when one exists. Absent a bearer this is no longer a local failure:
  // the session cookie is the normal credential, and the server decides.
  if (bearer) headers.authorization = `Bearer ${bearer}`
  const res = await fetch(path, { ...init, credentials: 'same-origin', headers })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let body: any = null
    try {
      body = JSON.parse(text)
    } catch {
      /* a non-JSON failure body is still worth showing, trimmed */
    }
    const wait = Number(res.headers.get('retry-after'))
    throw new ApiError(res.status, body, text, Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : 0)
  }
  // Anything the server accepted proves the credential works, which ends any
  // suspicion of a sign-in loop.
  clearBounce()
  const type = res.headers.get('content-type') || ''
  return (type.includes('json') ? res.json() : res.text()) as Promise<T>
}

/** Signing out is the server's business; the local dev token is ours. */
export async function signOut(): Promise<void> {
  try {
    await fetch('/v1/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } catch {
    // The cookie may well be gone regardless, and there is nowhere useful to
    // report this from; the sign-in page is the right destination either way.
  }
  forgetToken()
  clearBounce()
  location.replace('/login')
}
